import { Component } from '@theme/component';
import { renderConstructorPagination } from '@theme/constructor-pagination';
import { ThemeEvents } from '@theme/events';
import { isIOSDevice, prefersReducedMotion } from '@theme/utilities';
import { isFlatViewActive, setCardFlatImage } from '@theme/flat-view';
import {
  CONSTRUCTOR_ATTRS,
  CONSTRUCTOR_EVENTS,
  DEFAULT_BATCH_LITE_SECTION_ID,
  DEFAULT_BATCH_SECTION_ID,
  escapeHtml,
  extractHandleFromUrl,
  fetchBatchCards,
  getClonedCardFragment,
  toDomIdFragment,
  waitForConstructorClient,
} from '@theme/constructor-utils';

const RESULTS_PER_PAGE = 60;
const MAX_RESULTS_PER_PAGE = 60;
const MAX_BATCH_SIZE = 20;
const DESKTOP_BREAKPOINT_PX = 1080;
const POPULAR_SEARCH_TERMS_LIMIT = 8;
const REVEAL_FALLBACK_MS = 5000;

/**
 * @typedef {object} ConstructorResultItem
 * @property {string} id
 * @property {string} handle
 * @property {number} price
 * @property {string} title
 */

/**
 * @typedef {object} ConstructorPageData
 * @property {ConstructorResultItem[]} results
 * @property {number} total
 * @property {number} resultsPerPage
 * @property {any[]} refinedContent
 * @property {{ currentPage: number, totalPages: number, previousPage: number | null, nextPage: number | null }} paginationState
 * @property {NormalizedFacet[]} facets
 * @property {any[]} rawFacets
 * @property {NormalizedSortOption[]} sortOptions
 * @property {string | null} currentSort
 * @property {Map<string, DocumentFragment>} [cardMap]
 * @property {string} [redirectUrl] Set when the response contains a Constructor redirect rule; the browser is navigating and results should not be rendered.
 */

/**
 * @typedef {{ value?: string, display_name?: string, label?: string, count?: number, selected?: boolean, status?: { selected?: boolean } }} RawFacetValue
 */

/**
 * @typedef {{ name?: string, field?: string, display_name?: string, label?: string, data?: RawFacetValue[], options?: RawFacetValue[], values?: RawFacetValue[] }} RawFacet
 */

/**
 * @typedef {{ value: string, label: string, count: number, active: boolean }} NormalizedFacetValue
 */

/**
 * @typedef {{ field: string, label: string, values: NormalizedFacetValue[] }} NormalizedFacet
 */

/**
 * @typedef {{ value?: string, field?: string, sort_by?: string, direction?: string, sort_order?: string, display_name?: string, label?: string, selected?: boolean, status?: { selected?: boolean } }} RawSortOption
 */

/**
 * @typedef {{ value: string, label: string, selected: boolean }} NormalizedSortOption
 */

class ConstructorResultsList extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController | null} */
  #requestController = null;
  /** @type {number} */
  #requestGeneration = 0;
  /** @type {number} */
  #viewportWidth = window.innerWidth;
  /** @type {string} */
  #paginationCacheKey = '';
  /** @type {string[]} */
  #popularSearchTerms = [];
  /** @type {string} */
  #popularSearchResultId = '';
  /** @type {number} */
  #popularSearchNumResults = 0;
  /** @type {Promise<string[]> | null} */
  #popularSearchTermsPromise = null;
  /** @type {number | null} */
  #revealFallbackTimer = null;

  get searchQuery() {
    const q = new URLSearchParams(window.location.search).get('q');
    return q != null && q !== '' ? q : this.dataset.searchQuery || '';
  }

  get currentPage() {
    const page = Number.parseInt(
      new URLSearchParams(window.location.search).get('page') || '1',
      10,
    );
    return Number.isFinite(page) && page > 0 ? page : 1;
  }

  get requestedResultsPerPage() {
    const isDesktopViewport = this.#viewportWidth >= DESKTOP_BREAKPOINT_PX;
    const viewportSetting = isDesktopViewport
      ? this.dataset.productsPerPageDesktop
      : this.dataset.productsPerPageMobile;
    const parsed = Number.parseInt(viewportSetting || this.dataset.productsPerPage || '', 10);
    if (!Number.isFinite(parsed) || parsed <= 0) return RESULTS_PER_PAGE;
    return Math.min(parsed, MAX_RESULTS_PER_PAGE);
  }

  get sectionId() {
    return this.dataset.sectionId || 'search-results';
  }

  get searchRoute() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}search`;
  }

  get isBrowseMode() {
    return this.hasAttribute('data-cnstrc-browse') || this.dataset.context === 'collection';
  }

  get isSearchContext() {
    return this.dataset.context === 'search' || this.hasAttribute('data-cnstrc-search');
  }

  get browseGroupId() {
    const dataValue = (
      this.dataset.collectionHandle ||
      this.dataset.cnstrcFilterValue ||
      ''
    ).trim();
    if (dataValue) return dataValue;

    const parts = window.location.pathname.split('/').filter(Boolean);
    const collectionIndex = parts.indexOf('collections');
    if (collectionIndex === -1) return '';
    const handle = parts[collectionIndex + 1] || '';
    return decodeURIComponent(handle);
  }

  /**
   * Optional `custom.constructor_handle` collection metafield. When present the
   * browse request is keyed on `collection_id` instead of the default `group_id`.
   */
  get constructorHandle() {
    return (this.dataset.constructorHandle || '').trim();
  }

  get browseFilterName() {
    return this.constructorHandle ? 'collection_id' : 'group_id';
  }

  get browseFilterValue() {
    return this.constructorHandle || this.browseGroupId;
  }

  /** @returns {HTMLUListElement | null} */
  #getGrid() {
    const ref = this.refs.grid;
    const el = Array.isArray(ref) ? ref[0] : ref;
    return el instanceof HTMLUListElement ? el : null;
  }

  /** @returns {HTMLElement | null} */
  #getPagination() {
    const ref = this.refs.pagination;
    const el = Array.isArray(ref) ? ref[0] : ref;
    return el instanceof HTMLElement ? el : null;
  }

  connectedCallback() {
    // Re-attached elements should rehydrate from current URL state.
    if (this.hasAttribute(CONSTRUCTOR_ATTRS.initialized)) return;

    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    window.addEventListener('popstate', this.#handlePopState, { signal });
    window.addEventListener('resize', this.#handleViewportResize, { signal, passive: true });
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate, { signal });

    const pagination = this.#getPagination();
    pagination?.addEventListener('click', this.#handlePaginationClick, { signal });

    this.#viewportWidth = window.innerWidth;
    this.#fetchProducts();
    this.setAttribute(CONSTRUCTOR_ATTRS.initialized, '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#requestController?.abort();
    this.#clearRevealFallback();

    this.#abortController = null;
    this.#requestController = null;
    this.#paginationCacheKey = '';
    this.#popularSearchTermsPromise = null;
    if (this.isSearchContext) {
      document.documentElement.removeAttribute('data-cnstrc-search-empty-results');
      document.documentElement.removeAttribute('data-cnstrc-search-loading');
      document.documentElement.removeAttribute('data-cnstrc-search-has-results');
    }
    this.removeAttribute(CONSTRUCTOR_ATTRS.initialized);
  }

  #handleViewportResize = () => {
    this.#viewportWidth = window.innerWidth;
  };

  #getAbortSignal() {
    this.#requestController?.abort();
    this.#requestController = new AbortController();
    return this.#requestController.signal;
  }

  /** @param {number} generation */
  #isCurrentRequest(generation) {
    return generation === this.#requestGeneration;
  }

  #showLoading() {
    const grid = this.#getGrid();
    if (grid) {
      const hasInitialSsrCards =
        grid.querySelector('[data-cnstrc-ssr="true"]') instanceof HTMLElement;
      if (!hasInitialSsrCards) {
        this.#renderLoadingSlots(grid, this.#getLoadingSlotCount(grid));
      } else {
        grid.dataset.reveal = 'ready';
      }
      grid.setAttribute('aria-live', 'polite');
    }
    if (grid) grid.setAttribute('aria-busy', 'true');
    this.setAttribute('aria-busy', 'true');
    this.setAttribute('loading', '');
  }

  #hideLoading() {
    const grid = this.#getGrid();
    if (grid) grid.setAttribute('aria-busy', 'false');
    this.setAttribute('aria-busy', 'false');
    this.removeAttribute('loading');
  }

  #revealGrid() {
    this.#clearRevealFallback();
    const grid = this.#getGrid();
    if (grid) grid.dataset.reveal = 'ready';
  }

  #scheduleRevealFallback() {
    this.#clearRevealFallback();
    this.#revealFallbackTimer = window.setTimeout(() => {
      this.#revealFallbackTimer = null;
      this.#revealGrid();
    }, REVEAL_FALLBACK_MS);
  }

  #clearRevealFallback() {
    if (this.#revealFallbackTimer !== null) {
      clearTimeout(this.#revealFallbackTimer);
      this.#revealFallbackTimer = null;
    }
  }

  /** @param {boolean} visible */
  #setSearchNoResultsVisible(visible) {
    if (!this.isSearchContext) return;
    const noResults = document.querySelector('[data-cnstrc-search-no-results]');
    if (!(noResults instanceof HTMLElement)) return;
    noResults.toggleAttribute('hidden', !visible);
  }

  /** @param {boolean} visible */
  #setResultsListVisible(visible) {
    if (!this.isSearchContext) return;
    const resultsList = this.querySelector('#ResultsList');
    if (!(resultsList instanceof HTMLElement)) return;
    resultsList.toggleAttribute('hidden', !visible);
    const grid = this.#getGrid();
    if (grid) grid.toggleAttribute('hidden', !visible);
  }

  /** @param {boolean} visible */
  #setFacetsToggleVisible(visible) {
    if (!this.isSearchContext) return;
    const selectors =
      '#constructor-facets-toggle, .facets-toggle.facets-toggle--constructor-bar, .facets-toggle__sticky-bar';
    document.querySelectorAll(selectors).forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      element.toggleAttribute('hidden', !visible);
      if (!visible && element.classList.contains('facets-toggle__sticky-bar')) {
        element.setAttribute('data-stuck', 'false');
        element.setAttribute('aria-hidden', 'true');
      }
    });
  }

  /** @returns {HTMLElement | null} */
  #getPopularSearchContainer() {
    if (!this.isSearchContext) return null;
    const container = this.querySelector('[data-cnstrc-search-popular]');
    return container instanceof HTMLElement ? container : null;
  }

  /**
   * @param {string} query
   * @returns {string}
   */
  #buildSearchUrl(query) {
    const searchRoute = Theme?.routes?.search_url || '/search';
    const searchUrl = new URL(searchRoute, window.location.origin);
    searchUrl.searchParams.set('q', query);
    return searchUrl.toString();
  }

  /**
   * @param {any} item
   * @returns {string}
   */
  #extractPopularSearchTerm(item) {
    if (typeof item === 'string') return item.trim();
    if (!item || typeof item !== 'object') return '';

    const value =
      item?.value ??
      item?.text ??
      item?.query ??
      item?.term ??
      item?.data?.value ??
      item?.data?.text ??
      item?.data?.query ??
      item?.data?.searchTerm ??
      item?.data?.search_term ??
      item?.data?.name ??
      '';

    return typeof value === 'string' ? value.trim() : '';
  }

  /**
   * @param {any} response
   * @returns {string[]}
   */
  #normalizePopularSearchTerms(response) {
    const data = response?.response ?? response ?? {};
    const rawTerms = Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data?.recommendations)
        ? data.recommendations
        : Array.isArray(data?.data?.results)
          ? data.data.results
          : [];

    /** @type {string[]} */
    const normalizedTerms = [];
    const seen = new Set();

    for (const item of rawTerms) {
      const term = this.#extractPopularSearchTerm(item);
      if (!term) continue;
      const key = term.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      normalizedTerms.push(term);
    }

    return normalizedTerms;
  }

  /**
   * @param {AbortSignal} signal
   * @returns {Promise<string[]>}
   */
  async #fetchPopularSearchTerms(signal) {
    const client = await waitForConstructorClient({
      signal,
      predicate: (constructorClient) =>
        Boolean(constructorClient?.recommendations?.getRecommendations),
    });
    if (!client?.recommendations?.getRecommendations || signal.aborted) return [];

    const response = await client.recommendations.getRecommendations('popular-searches', {
      section: 'Search Suggestions',
    });
    if (signal.aborted) return [];

    this.#popularSearchResultId = response?.result_id || '';
    this.#popularSearchNumResults = Number(response?.response?.total_num_results || 0);
    return this.#normalizePopularSearchTerms(response).slice(0, POPULAR_SEARCH_TERMS_LIMIT);
  }

  /**
   * @param {AbortSignal} signal
   * @returns {Promise<string[]>}
   */
  async #ensurePopularSearchTerms(signal) {
    if (this.#popularSearchTerms.length) return this.#popularSearchTerms;
    if (this.#popularSearchTermsPromise) return this.#popularSearchTermsPromise;

    this.#popularSearchTermsPromise = this.#fetchPopularSearchTerms(signal)
      .then((terms) => {
        if (signal.aborted) return [];
        this.#popularSearchTerms = terms;
        return terms;
      })
      .catch(() => [])
      .finally(() => {
        this.#popularSearchTermsPromise = null;
      });

    return this.#popularSearchTermsPromise;
  }

  /**
   * @param {string[]} terms
   */
  #renderPopularSearchesSection(terms) {
    const container = this.#getPopularSearchContainer();
    if (!container) return;
    if (!terms.length) {
      container.innerHTML = '';
      container.setAttribute('hidden', '');
      return;
    }

    const title = String(
      Theme?.translations?.content_popular_searches ||
        Theme?.translations?.content_top_suggestions ||
        'Popular Searches',
    );

    container.innerHTML = `
      <div class="cnstrc-predictive-search__trending-container
            cnstrc-predictive-search__popular-container"
            data-cnstrc-recommendations
            data-cnstrc-recommendations-pod-id="popular-searches"
            data-cnstrc-popular-searches
            data-cnstrc-result-id="${escapeHtml(this.#popularSearchResultId)}"
            data-cnstrc-num-results="${escapeHtml(this.#popularSearchNumResults)}"
          >
        <div class="cnstrc-predictive-search__suggestions cnstrc-predictive-search__trending">
          <h3 class="cnstrc-predictive-search__section-title h2">${escapeHtml(title)}</h3>
          <ul class="cnstrc-predictive-search__suggestions-list" role="listbox">
            ${terms
              .map((term, index) => {
                const safeTerm = escapeHtml(term);
                return `
                  <li
                    class="cnstrc-predictive-search__suggestion-item"
                    role="option"
                    data-query="${safeTerm}"
                    data-cnstrc-popular-item="true"
                    data-result-count="0"
                    data-index="${index}"
                    aria-selected="false"
                    data-cnstrc-item="recommendation"
                    data-cnstrc-item-id="${safeTerm}"
                    data-cnstrc-item-name="${safeTerm}"
                    data-cnstrc-item-section="Search Suggestions"
                    data-cnstrc-strategy-id="bestsellers"
                  >
                    <a
                      href="${this.#buildSearchUrl(term)}"
                      class="cnstrc-predictive-search__suggestion-link"
                    >
                      ${safeTerm}
                    </a>
                  </li>
                `;
              })
              .join('')}
          </ul>
        </div>
      </div>
    `;
    container.removeAttribute('hidden');
  }

  /**
   * @param {'loading' | 'results' | 'no-results' | 'error'} state
   * @param {AbortSignal} signal
   */
  async #setSearchUiState(state, signal) {
    if (!this.isSearchContext) return;

    const isLoading = state === 'loading';
    const hasResults = state === 'results';
    const hasNoResults = state === 'no-results';
    document.documentElement.toggleAttribute('data-cnstrc-search-loading', isLoading);
    document.documentElement.toggleAttribute('data-cnstrc-search-empty-results', hasNoResults);
    document.documentElement.toggleAttribute('data-cnstrc-search-has-results', hasResults);
    this.#setSearchNoResultsVisible(hasNoResults);
    this.#setResultsListVisible(!hasNoResults);
    // Keep facets hidden until we have a confirmed non-empty Constructor response.
    this.#setFacetsToggleVisible(hasResults);

    if (!hasNoResults) {
      this.#renderPopularSearchesSection([]);
      return;
    }

    const terms = await this.#ensurePopularSearchTerms(signal);
    if (signal.aborted) return;
    this.#renderPopularSearchesSection(terms);
  }

  /**
   * Estimate loading placeholders count from current grid state.
   * @param {HTMLUListElement} grid
   */
  #getLoadingSlotCount(grid) {
    const existingCount = grid.querySelectorAll('[ref="cards[]"]').length;
    if (existingCount > 0) {
      return Math.min(existingCount, this.requestedResultsPerPage);
    }
    return this.requestedResultsPerPage;
  }

  /**
   * Render temporary placeholders while Constructor API request is in flight.
   * @param {HTMLUListElement} grid
   * @param {number} count
   */
  #renderLoadingSlots(grid, count) {
    if (!Number.isFinite(count) || count <= 0) return;
    grid.innerHTML = this.#buildCardSlotsMarkup(
      Array.from({ length: count }, (_, index) => ({
        id: `loading-${index}`,
        handle: '',
        price: 0,
        title: '',
      })),
      this.currentPage,
    );
  }

  /**
   * Smooth scroll to top before fetching a different result page/context.
   */
  #scrollViewportToTop() {
    if (window.scrollY <= 0) return;

    document.documentElement.setAttribute('data-scrolling-to-top', '');
    const behavior = prefersReducedMotion() || isIOSDevice() ? 'instant' : 'smooth';
    window.scrollTo({ top: 0, behavior });

    if (behavior === 'instant') {
      document.documentElement.removeAttribute('data-scrolling-to-top');
      return;
    }

    const clearScrollingState = () =>
      document.documentElement.removeAttribute('data-scrolling-to-top');
    if ('onscrollend' in window) {
      window.addEventListener('scrollend', clearScrollingState, { once: true });
      return;
    }

    setTimeout(clearScrollingState, 450);
  }

  /** @param {number} total */
  #updateSearchResultsCount(total) {
    const countValue = String(Number.isFinite(total) ? total : 0);
    this.setAttribute('data-cnstrc-num-results', countValue);

    const headingEl = document.getElementById('search-results-heading');
    if (headingEl) {
      headingEl.removeAttribute('hidden');
    }

    const countEl = document.getElementById('search-results-count');
    if (countEl) {
      countEl.textContent = countValue;
      countEl.removeAttribute('hidden');
    }

    const filterCountEls = document.querySelectorAll('[data-constructor-filter-count]');
    filterCountEls.forEach((el) => {
      const template = el.getAttribute('data-template') || '';
      const content = template.includes('__COUNT__')
        ? template.replace('__COUNT__', countValue)
        : countValue;

      el.textContent = content;
      el.removeAttribute('hidden');
    });

    const seeResultsEls = document.querySelectorAll('[data-constructor-see-results]');
    seeResultsEls.forEach((el) => {
      const template = el.getAttribute('data-template') || '';
      const content = template.includes('__COUNT__')
        ? template.replace('__COUNT__', countValue)
        : countValue;
      el.textContent = content;
    });
  }

  /** @param {any[]} results */
  #normalizeResults(results) {
    /** @type {ConstructorResultItem[]} */
    const normalized = [];
    results.forEach((result, index) => {
      const data = result?.data || {};
      const handle = extractHandleFromUrl(result?.url || data.url || data.product_url || '');
      if (!handle) return;
      normalized.push({
        id: data.id || result?.value || `${handle}-${index}`,
        handle,
        price: data.price,
        title: result.value,
      });
    });
    return normalized;
  }

  /**
   * @param {{ total: number, currentPage: number, resultsPerPage: number }} params
   */
  #computePaginationState({ total, currentPage, resultsPerPage }) {
    const totalPages = Math.ceil(total / resultsPerPage);
    return {
      currentPage,
      totalPages,
      previousPage: currentPage > 1 ? currentPage - 1 : null,
      nextPage: currentPage < totalPages ? currentPage + 1 : null,
    };
  }

  /**
   * Build Constructor filters payload from URL `filter.*` params.
   * @returns {Record<string, string[]>}
   */
  #getFiltersFromUrl() {
    /** @type {Record<string, string[]>} */
    const filters = {};
    const params = new URLSearchParams(window.location.search);

    params.forEach((value, key) => {
      if (!key.startsWith('filter.')) return;
      const field = key.slice('filter.'.length);
      if (!field) return;
      if (!filters[field]) filters[field] = [];
      filters[field].push(value);
    });

    return filters;
  }

  /**
   * @returns {string}
   */
  #getSortByFromUrl() {
    const value = new URLSearchParams(window.location.search).get('sort_by');
    return typeof value === 'string' ? value.trim() : '';
  }

  /**
   * @param {string} sortBy
   */
  #buildSortRequest(sortBy) {
    if (!sortBy || sortBy === 'relevance') return {};

    if (sortBy.endsWith('-asc')) {
      return {
        sortBy: sortBy.slice(0, -4),
        sortOrder: 'ascending',
      };
    }

    if (sortBy.endsWith('-desc')) {
      return {
        sortBy: sortBy.slice(0, -5),
        sortOrder: 'descending',
      };
    }

    return { sortBy };
  }

  /**
   * @param {RawSortOption[]} rawSortOptions
   * @param {string} currentSortFromUrl
   * @returns {NormalizedSortOption[]}
   */
  #normalizeSortOptions(rawSortOptions, currentSortFromUrl) {
    if (!Array.isArray(rawSortOptions)) return [];

    const seen = new Set();
    /** @type {NormalizedSortOption[]} */
    const options = [];

    rawSortOptions.forEach((option) => {
      const directionRaw = String(option?.sort_order || option?.direction || '').toLowerCase();
      const normalizedDirection =
        directionRaw === 'ascending' || directionRaw === 'asc'
          ? 'asc'
          : directionRaw === 'descending' || directionRaw === 'desc'
            ? 'desc'
            : '';

      const sortField = String(option?.sort_by || option?.field || '').trim();
      const composedValue =
        sortField && normalizedDirection ? `${sortField}-${normalizedDirection}` : sortField;
      const value = String(option?.value || composedValue || '').trim();
      if (!value || seen.has(value)) return;
      seen.add(value);

      const label = String(option?.display_name || option?.label || value);
      const selected =
        (currentSortFromUrl && value === currentSortFromUrl) ||
        Boolean(option?.selected || option?.status?.selected);

      options.push({
        value,
        label,
        selected,
      });
    });

    if (!options.length) return [];
    if (options.some((option) => option.selected)) return options;

    return options.map((option, index) => ({
      ...option,
      selected: index === 0,
    }));
  }

  /**
   * Normalize Constructor facet payload into the structure consumed by existing facet UI.
   * @param {RawFacet[]} rawFacets
   * @param {Record<string, string[]>} activeFilters
   * @returns {NormalizedFacet[]}
   */
  #normalizeFacets(rawFacets, activeFilters) {
    if (!Array.isArray(rawFacets)) return [];
    /** @type {NormalizedFacet[]} */
    const normalized = [];

    rawFacets.forEach((facet) => {
      const field = String(facet?.name || facet?.field || '').trim();
      if (!field) return;

      const label = String(facet?.display_name || facet?.label || field).trim();
      const rawValues = Array.isArray(facet?.data)
        ? facet.data
        : Array.isArray(facet?.options)
          ? facet.options
          : Array.isArray(facet?.values)
            ? facet.values
            : [];

      const selectedValues = activeFilters[field] || [];
      /** @type {NormalizedFacetValue[]} */
      const values = [];

      rawValues.forEach((rawValue) => {
        const value = rawValue?.value == null ? '' : String(rawValue.value);
        if (!value) return;

        const count = Number(rawValue?.count ?? 0);
        values.push({
          value,
          label: String(rawValue?.display_name || rawValue?.label || value),
          count: Number.isFinite(count) ? count : 0,
          active:
            Boolean(rawValue?.selected || rawValue?.status?.selected) ||
            selectedValues.includes(value),
        });
      });

      normalized.push({
        field,
        label,
        values,
      });
    });

    return normalized;
  }

  /** @param {AbortSignal} signal */
  async #waitForConstructorClientForContext(signal) {
    const expectsBrowseClient = this.isBrowseMode && Boolean(this.browseFilterValue);
    return waitForConstructorClient({
      signal,
      predicate: (client) =>
        expectsBrowseClient
          ? Boolean(client?.browse?.getBrowseResults)
          : Boolean(client?.search?.getSearchResults),
    });
  }

  /**
   * Reuse an inline prefetch response for default collection browse requests
   * so the first API round-trip can start before the SDK is ready.
   * @param {{ shouldUseBrowse: boolean, browseFilterName: string, browseFilterValue: string, page: number, requestedResultsPerPage: number, filters: Record<string, string[]>, currentSortFromUrl: string, signal: AbortSignal }} params
   * @returns {Promise<any | null>}
   */
  async #consumePrefetchedBrowseResponse({
    shouldUseBrowse,
    browseFilterName,
    browseFilterValue,
    page,
    requestedResultsPerPage,
    filters,
    currentSortFromUrl,
    signal,
  }) {
    if (!shouldUseBrowse) return null;
    if (page !== 1) return null;
    if (requestedResultsPerPage !== RESULTS_PER_PAGE) return null;
    if (Object.keys(filters).length > 0) return null;
    if (currentSortFromUrl) return null;

    const prefetchEntry = /** @type {any} */ (window).__constructorBrowsePrefetch;
    if (!prefetchEntry || prefetchEntry.consumed) return null;
    if (prefetchEntry.filterName !== browseFilterName) return null;
    if (prefetchEntry.filterValue !== browseFilterValue) return null;
    if (prefetchEntry.page !== page) return null;
    if (prefetchEntry.resultsPerPage !== requestedResultsPerPage) return null;
    if (!(prefetchEntry.promise instanceof Promise)) return null;

    prefetchEntry.consumed = true;

    try {
      const prefetchedResponse = await prefetchEntry.promise;
      if (signal.aborted) return null;
      if (!prefetchedResponse || !Array.isArray(prefetchedResponse?.response?.results)) return null;
      return prefetchedResponse;
    } catch {
      return null;
    }
  }

  /**
   * @param {{ query: string, page: number, requestedResultsPerPage: number, signal: AbortSignal }} params
   * @returns {Promise<ConstructorPageData>}
   */
  async #loadResults({ query, page, requestedResultsPerPage, signal }) {
    const filters = this.#getFiltersFromUrl();
    const currentSortFromUrl = this.#getSortByFromUrl();
    const isBrowseMode = this.isBrowseMode;
    const browseFilterName = this.browseFilterName;
    const browseFilterValue = this.browseFilterValue;
    const shouldUseBrowse = isBrowseMode && Boolean(browseFilterValue);

    const requestOptions = {
      page,
      resultsPerPage: requestedResultsPerPage,
      ...(Object.keys(filters).length > 0 ? { filters } : {}),
      ...this.#buildSortRequest(currentSortFromUrl),
      fmtOptions: {
        hidden_facets: ['jeans_sizing'],
      },
    };
    const prefetchedResponse = await this.#consumePrefetchedBrowseResponse({
      shouldUseBrowse,
      browseFilterName,
      browseFilterValue,
      page,
      requestedResultsPerPage,
      filters,
      currentSortFromUrl,
      signal,
    });

    let constructorioClient = null;
    if (!prefetchedResponse) {
      constructorioClient = await this.#waitForConstructorClientForContext(signal);
      if (
        (shouldUseBrowse && !constructorioClient?.browse?.getBrowseResults) ||
        (!shouldUseBrowse && !constructorioClient?.search?.getSearchResults)
      ) {
        return {
          results: [],
          total: 0,
          resultsPerPage: requestedResultsPerPage,
          paginationState: {
            currentPage: page,
            totalPages: 0,
            previousPage: null,
            nextPage: null,
          },
          refinedContent: [],
          facets: [],
          rawFacets: [],
          sortOptions: [],
          currentSort: null,
        };
      }
    }

    const response =
      prefetchedResponse ||
      (shouldUseBrowse
        ? await constructorioClient.browse.getBrowseResults(
            browseFilterName,
            browseFilterValue,
            requestOptions,
          )
        : await constructorioClient.search.getSearchResults(query, requestOptions));

    const redirectUrl = response?.response?.redirect?.data?.url;
    if (redirectUrl) {
      window.location.href = redirectUrl;
      return {
        redirectUrl,
        results: [],
        total: 0,
        resultsPerPage: requestedResultsPerPage,
        refinedContent: [],
        paginationState: {
          currentPage: page,
          totalPages: 0,
          previousPage: null,
          nextPage: null,
        },
        facets: [],
        rawFacets: [],
        sortOptions: [],
        currentSort: null,
      };
    }

    /** @type {any[]} */
    const rawResults = (response?.response?.results ?? []).slice(0, requestedResultsPerPage);
    const normalizedResults = this.#normalizeResults(rawResults);
    const total = Number(response?.response?.total_num_results ?? 0);
    const responseResultsPerPage = Number(response?.response?.results_per_page ?? 0);
    const rawRefinedContent = response?.response?.refined_content;
    const refinedContent = Array.isArray(rawRefinedContent)
      ? rawRefinedContent
      : Array.isArray(rawRefinedContent?.data)
        ? rawRefinedContent.data
        : Array.isArray(rawRefinedContent?.content)
          ? rawRefinedContent.content
          : [];
    const resultsPerPage =
      Number.isFinite(responseResultsPerPage) && responseResultsPerPage > 0
        ? responseResultsPerPage
        : requestedResultsPerPage;
    /** @type {RawFacet[]} */
    const rawFacets = Array.isArray(response?.response?.facets) ? response.response.facets : [];
    const normalizedFacets = this.#normalizeFacets(rawFacets, filters);
    /** @type {RawSortOption[]} */
    const rawSortOptions = Array.isArray(response?.response?.sort_options)
      ? response.response.sort_options
      : [];
    const sortOptions = this.#normalizeSortOptions(rawSortOptions, currentSortFromUrl);
    const currentSort = sortOptions.find((option) => option.selected)?.value || null;
    return {
      results: normalizedResults,
      total,
      resultsPerPage,
      refinedContent,
      paginationState: this.#computePaginationState({ total, currentPage: page, resultsPerPage }),
      facets: normalizedFacets,
      rawFacets,
      sortOptions,
      currentSort,
    };
  }

  /**
   * @param {ConstructorResultItem[]} results
   * @param {number} size
   */
  #chunkResults(results, size) {
    const chunks = [];
    for (let i = 0; i < results.length; i += size) {
      chunks.push(results.slice(i, i + size));
    }
    return chunks;
  }

  /** @param {number} totalResults */
  #getPriorityBatchSize(totalResults) {
    if (this.#viewportWidth < 750) return Math.min(totalResults, 8);
    if (this.#viewportWidth < 990) return Math.min(totalResults, 12);
    return Math.min(totalResults, MAX_BATCH_SIZE);
  }

  /** @param {number} page */
  #getPageUrl(page) {
    const url = new URL(window.location.href);
    if (page <= 1) url.searchParams.delete('page');
    else url.searchParams.set('page', String(page));
    return url.toString();
  }

  /**
   * @param {ConstructorResultItem[]} results
   * @param {number} currentPage
   * @returns {HTMLElement[]}
   */
  #renderCardSlots(results, currentPage) {
    const grid = this.#getGrid();
    if (!grid) return [];

    grid.innerHTML = this.#buildCardSlotsMarkup(results, currentPage);
    grid.dataset.reveal = 'pending';

    return Array.from(grid.querySelectorAll(`[${CONSTRUCTOR_ATTRS.cardSlotIndex}]`)).filter(
      (el) => el instanceof HTMLElement,
    );
  }

  /**
   * @param {ConstructorResultItem[]} results
   * @param {number} currentPage
   */
  #buildCardSlotsMarkup(results, currentPage) {
    return results
      .map(
        (result, index) => `
          <li
            id="${this.sectionId}-${toDomIdFragment(result.id)}"
            class="product-grid__item product-grid__item--${index} cnstrc-card-slot"
            data-page="${currentPage}"
            data-product-handle="${escapeHtml(result.handle)}"
            data-cnstrc-slot-index="${index}"
            data-cnstrc-item-section="Products"
            data-cnstrc-item-id="${result.id}"
            data-cnstrc-item-price="${result.price}"
            data-cnstrc-item-name="${result.title}"
            data-state="loading"
            aria-busy="true"
            aria-hidden="true"
            ref="cards[]"
          >
            <div class="cnstrc-card-slot__skeleton product-grid__card" aria-hidden="true">
              <div class="cnstrc-card-slot__skeleton-square"></div>
            </div>
          </li>
        `,
      )
      .join('');
  }

  /**
   * @param {HTMLElement} slot
   * @param {string} handle
   */
  #renderSlotError(slot, handle) {
    const href = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}`;
    slot.innerHTML = `
      <a class="cnstrc-card-slot__fallback" href="${href}">
        ${escapeHtml(handle)}
      </a>
    `;
    slot.classList.remove('cnstrc-card-slot');
    slot.setAttribute('aria-busy', 'false');
    slot.setAttribute('data-state', 'error');
    slot.removeAttribute('aria-hidden');
  }

  /**
   * @param {HTMLElement[]} slots
   * @param {ConstructorResultItem[]} batch
   * @param {Map<string, DocumentFragment>} cardMap
   * @param {number} offset
   */
  #hydrateBatchSlots(slots, batch, cardMap, offset) {
    let hydratedCount = 0;
    batch.forEach((result, index) => {
      const slot = slots[offset + index];
      if (!slot) return;

      const fragment = getClonedCardFragment(cardMap, result.handle);
      if (!fragment) {
        this.#renderSlotError(slot, result.handle);
        return;
      }

      // VBR-314: with flat view on, swap the fragment's first slide to the flat
      // image BEFORE inserting it, so the regular image is never painted — no
      // flash when paginating. Idempotent with the productsRendered reapply.
      if (isFlatViewActive()) {
        const cardGallery = fragment.querySelector('.card-gallery');
        if (cardGallery) setCardFlatImage(cardGallery, true);
      }

      slot.replaceChildren(fragment);
      slot.classList.remove('cnstrc-card-slot');
      slot.setAttribute('aria-busy', 'false');
      slot.setAttribute('data-state', 'loaded');
      slot.removeAttribute('aria-hidden');
      hydratedCount += 1;
    });
    return hydratedCount;
  }

  /**
   * @param {HTMLElement[]} slots
   * @param {ConstructorResultItem[]} batch
   * @param {number} offset
   */
  #markBatchFailed(slots, batch, offset) {
    batch.forEach((result, index) => {
      const slot = slots[offset + index];
      if (!slot) return;
      this.#renderSlotError(slot, result.handle);
    });
  }

  /**
   * @param {ConstructorResultItem[]} batch
   * @param {AbortSignal} signal
   * @param {string} [sectionId]
   */
  async #fetchCardsForBatch(batch, signal, sectionId = DEFAULT_BATCH_SECTION_ID) {
    // fetchBatchCards performs in-flight dedupe + short-lived memoization for
    // repeated handle sets, which reduces duplicate Section Rendering requests.
    return fetchBatchCards({
      handles: batch.map((result) => result.handle),
      signal,
      searchRoute: this.searchRoute,
      sectionId,
    });
  }

  /**
   * @param {{ facets?: NormalizedFacet[], rawFacets?: RawFacet[], sortOptions?: NormalizedSortOption[], currentSort?: string | null, totalResults?: number }} payload
   */
  #emitFacetEvents({
    facets = [],
    rawFacets = [],
    sortOptions = [],
    currentSort = null,
    totalResults = 0,
  }) {
    const facetDetail = {
      facets,
      constructorFacets: rawFacets,
    };

    document.dispatchEvent(
      new CustomEvent('cnstrc:facets-update', {
        detail: facetDetail,
      }),
    );
    document.dispatchEvent(
      new CustomEvent('cnstrc:results-loaded', {
        detail: { facets },
      }),
    );

    document.dispatchEvent(
      new CustomEvent('cnstrc:sorting-update', {
        detail: {
          options: sortOptions,
          current: currentSort,
          totalResults,
        },
      }),
    );
  }

  #renderNoResults() {
    const grid = this.#getGrid();
    if (!grid) return;

    const message = String(Theme?.translations?.content_no_products_found || 'No products found.');
    grid.innerHTML = `
      <li class="cnstrc-no-results">
        <div class="cnstrc-no-results__content">
          <p class="cnstrc-no-results__title">${escapeHtml(message)}</p>
        </div>
      </li>
    `;
    this.#revealGrid();
  }

  #renderError() {
    const grid = this.#getGrid();
    if (!grid) return;
    grid.innerHTML = `
      <li class="cnstrc-error">
        <div class="cnstrc-error__content">
          <p>Something went wrong loading products.</p>
        </div>
      </li>
    `;
    this.#revealGrid();
  }

  /**
   * @param {{ currentPage: number, totalPages: number, previousPage: number | null, nextPage: number | null }} paginationState
   */
  #renderPagination(paginationState) {
    const pagination = this.#getPagination();
    if (!pagination) return;

    if (!paginationState || paginationState.totalPages <= 1) {
      pagination.hidden = true;
      pagination.innerHTML = '';
      this.#paginationCacheKey = '';
      return;
    }

    const key = JSON.stringify(paginationState);
    if (key === this.#paginationCacheKey) {
      pagination.hidden = false;
      return;
    }
    this.#paginationCacheKey = key;

    pagination.hidden = false;
    pagination.innerHTML = renderConstructorPagination({
      paginationState,
      getPageUrl: (page) => this.#getPageUrl(page),
      labels: {
        backToTop: String(Theme?.translations?.accessibility_back_to_top || 'Back to top'),
        previous: String(Theme?.translations?.pagination_previous || 'Previous'),
        next: String(Theme?.translations?.pagination_next || 'Next'),
      },
    });
  }

  /** @param {number} page */
  goToPage(page) {
    if (this.hasAttribute('loading')) return;
    if (!Number.isFinite(page) || page < 1) return;
    history.pushState({}, '', this.#getPageUrl(page));
    this.#scrollViewportToTop();
    this.#fetchProducts();
  }

  /**
   * Keep card image srcset sizing aligned with mobile grid density.
   * Two-column mobile layout should not request one-column image widths.
   *
   * @param {string} layout
   */
  #syncCardImageSizes(layout = 'two') {
    const grid = this.#getGrid();
    if (!grid) return;

    const mobileSize = layout === 'one' ? '100vw' : '50vw';
    const responsiveSizes = `(min-width: 1024px) 25vw, ${mobileSize}`;
    grid.querySelectorAll('img.product-media__image').forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;
      image.setAttribute('sizes', responsiveSizes);
    });
  }

  async #fetchProducts() {
    const generation = ++this.#requestGeneration;
    const signal = this.#getAbortSignal();
    this.#clearRevealFallback();
    const query = this.searchQuery;
    const isBrowseMode = this.isBrowseMode;
    const browseFilterValue = this.browseFilterValue;
    const currentPage = this.currentPage;
    const requestedResultsPerPage = this.requestedResultsPerPage;
    const hasInitialSsrCards =
      this.#getGrid()?.querySelector('[data-cnstrc-ssr="true"]') instanceof HTMLElement;
    const shouldKeepSsrUntilFirstBatch =
      hasInitialSsrCards &&
      isBrowseMode &&
      currentPage === 1 &&
      Object.keys(this.#getFiltersFromUrl()).length === 0 &&
      !this.#getSortByFromUrl();

    this.#showLoading();
    void this.#setSearchUiState('loading', signal);

    try {
      if (!query && !(isBrowseMode && browseFilterValue)) {
        this.#emitFacetEvents({
          facets: [],
          rawFacets: [],
          sortOptions: [],
          currentSort: null,
          totalResults: 0,
        });
        this.#updateSearchResultsCount(0);
        this.#renderNoResults();
        this.#renderPagination({
          currentPage,
          totalPages: 0,
          previousPage: null,
          nextPage: null,
        });
        void this.#setSearchUiState('results', signal);
        return;
      }

      const pageData = await this.#loadResults({
        query,
        page: currentPage,
        requestedResultsPerPage,
        signal,
      });
      if (signal.aborted || !this.#isCurrentRequest(generation)) return;

      if (pageData.redirectUrl) return;

      this.#emitFacetEvents({
        facets: pageData.facets,
        rawFacets: pageData.rawFacets,
        sortOptions: pageData.sortOptions,
        currentSort: pageData.currentSort,
        totalResults: pageData.total,
      });
      this.#updateSearchResultsCount(pageData.total);
      if (pageData.total === 0) {
        this.#renderNoResults();
        this.#renderPagination(pageData.paginationState);
        await this.#setSearchUiState('no-results', signal);
        return;
      }

      if (!pageData.results.length) {
        this.#renderError();
        this.#renderPagination(pageData.paginationState);
        void this.#setSearchUiState('error', signal);
        return;
      }

      void this.#setSearchUiState('results', signal);
      /** @type {any} */ (window).__cnstrcHotspotPayload = {
        refinedContent: pageData.refinedContent,
        currentPage,
      };

      /** @type {HTMLElement[]} */
      let slots = [];
      let hasRenderedSlots = false;
      const renderSlots = () => {
        if (hasRenderedSlots) return;
        slots = this.#renderCardSlots(pageData.results, currentPage);
        this.dispatchEvent(
          new CustomEvent(CONSTRUCTOR_EVENTS.slotsRendered, {
            bubbles: true,
            detail: {
              container: this.#getGrid(),
              refinedContent: pageData.refinedContent,
              currentPage,
            },
          }),
        );
        this.#scheduleRevealFallback();
        hasRenderedSlots = true;
      };

      const priorityBatchSize = this.#getPriorityBatchSize(pageData.results.length);
      const firstBatch = pageData.results.slice(0, priorityBatchSize);
      const remainingBatches = this.#chunkResults(
        pageData.results.slice(priorityBatchSize),
        MAX_BATCH_SIZE,
      );

      if (!shouldKeepSsrUntilFirstBatch) {
        renderSlots();
      }

      const firstBatchPromise = this.#fetchCardsForBatch(
        firstBatch,
        signal,
        DEFAULT_BATCH_LITE_SECTION_ID,
      );

      const firstBatchMap = await firstBatchPromise;
      if (signal.aborted || !this.#isCurrentRequest(generation)) return;
      if (!hasRenderedSlots) {
        renderSlots();
      }

      const firstHydrated = this.#hydrateBatchSlots(slots, firstBatch, firstBatchMap, 0);
      if (firstHydrated === 0) {
        this.#renderError();
        this.#renderPagination(pageData.paginationState);
        return;
      }
      this.#syncCardImageSizes(this.#getGrid()?.getAttribute('product-grid-view') || 'two');
      this.#revealGrid();

      const remainingSettled = await Promise.allSettled(
        remainingBatches.map((batch) => this.#fetchCardsForBatch(batch, signal)),
      );
      if (signal.aborted || !this.#isCurrentRequest(generation)) return;

      remainingSettled.forEach((settled, batchIndex) => {
        const offset = priorityBatchSize + batchIndex * MAX_BATCH_SIZE;
        const batch = remainingBatches[batchIndex];
        if (!batch) return;

        if (settled.status === 'fulfilled') {
          this.#hydrateBatchSlots(slots, batch, settled.value, offset);
        } else {
          this.#markBatchFailed(slots, batch, offset);
        }
      });

      if (signal.aborted || !this.#isCurrentRequest(generation)) return;

      this.dispatchEvent(
        new CustomEvent(CONSTRUCTOR_EVENTS.productsRendered, {
          bubbles: true,
          detail: {
            count: pageData.results.length,
            container: this.#getGrid(),
            refinedContent: pageData.refinedContent,
            currentPage,
          },
        }),
      );
      this.#renderPagination(pageData.paginationState);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      if (!this.#isCurrentRequest(generation)) return;
      console.error('[Constructor] search failed', error);
      this.#renderError();
      void this.#setSearchUiState('error', signal);
      this.#renderPagination({
        currentPage,
        totalPages: 0,
        previousPage: null,
        nextPage: null,
      });
    } finally {
      if (this.#isCurrentRequest(generation)) this.#hideLoading();
    }
  }

  #handleFilterUpdate = () => {
    this.#scrollViewportToTop();
    this.#fetchProducts();
  };

  #handlePopState = () => {
    this.#scrollViewportToTop();
    this.#fetchProducts();
  };

  /** @param {MouseEvent} event */
  #handlePaginationClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    const pageLink = target.closest?.('[data-cnstrc-page]');
    if (!pageLink) return;
    event.preventDefault();
    const page = Number.parseInt(
      /** @type {HTMLElement} */ (pageLink).dataset.cnstrcPage || '',
      10,
    );
    if (page) this.goToPage(page);
  };
}

if (!customElements.get('constructor-results-list')) {
  customElements.define('constructor-results-list', ConstructorResultsList);
}

export { ConstructorResultsList };
